import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Share2, Loader2, Eye, AlertTriangle, Copy, CheckCircle2, ExternalLink } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { adminListAllPrayers, adminListUsers } from '../lib/api';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin · Link Preview tester
 *
 * `/admin/link-preview` — shows exactly what a shared prayer or circle
 * link's social card will look like BEFORE sharing it. Fetches the real
 * HTML served by the crawler endpoints (/api/p/:id, /api/c/:userId),
 * parses the Open Graph tags, and renders them as mock cards in the two
 * most common client styles (large image + compact summary).
 */

// Pull the id out of a pasted share link, SPA route, or raw id.
const extractId = (raw, mode) => {
  const v = (raw || '').trim();
  if (!v) return null;
  const patterns = mode === 'prayer'
    ? [/\/api\/p\/([\w-]+)/, /\/prayer\/([\w-]+)/]
    : [/\/api\/c\/([\w-]+)/, /\/circle\/([\w-]+)/];
  for (const re of patterns) {
    const m = v.match(re);
    if (m) return m[1];
  }
  // Raw id — strip any query string the user pasted along.
  return v.split('?')[0].split('/').pop() || null;
};

const parseMeta = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const get = (sel, attr = 'content') => doc.querySelector(sel)?.getAttribute(attr) || '';
  return {
    title: get('meta[property="og:title"]'),
    description: get('meta[property="og:description"]'),
    image: get('meta[property="og:image"]'),
    url: get('meta[property="og:url"]'),
    siteName: get('meta[property="og:site_name"]'),
    type: get('meta[property="og:type"]'),
    twitterCard: get('meta[name="twitter:card"]'),
  };
};

const AdminLinkPreview = () => {
  const { user, loading: authLoading } = useAuth();

  const [mode, setMode] = useState('prayer'); // 'prayer' | 'circle'
  const [prayers, setPrayers] = useState([]);
  const [users, setUsers] = useState([]);
  const [pickedId, setPickedId] = useState('');
  const [pasted, setPasted] = useState('');
  const [listLoading, setListLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState(null);
  const [previewedUrl, setPreviewedUrl] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    let cancelled = false;
    (async () => {
      setListLoading(true);
      try {
        const [p, u] = await Promise.all([adminListAllPrayers(), adminListUsers()]);
        if (cancelled) return;
        setPrayers(Array.isArray(p?.items) ? p.items : []);
        setUsers(Array.isArray(u) ? u : []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message || 'Failed to load pick lists');
      } finally {
        if (!cancelled) setListLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Switching mode clears the previous selection + rendered card.
  useEffect(() => {
    setPickedId('');
    setPasted('');
    setMeta(null);
    setPreviewedUrl('');
    setError(null);
  }, [mode]);

  const targetId = useMemo(
    () => (pasted.trim() ? extractId(pasted, mode) : pickedId || null),
    [pasted, pickedId, mode],
  );

  const shareUrl = targetId
    ? `${BACKEND}/api/${mode === 'prayer' ? 'p' : 'c'}/${targetId}${mode === 'prayer' ? '?ref=share' : ''}`
    : '';

  const doPreview = async () => {
    if (!targetId) return;
    setBusy(true);
    setError(null);
    setMeta(null);
    setCopied(false);
    try {
      const endpoint = `${BACKEND}/api/${mode === 'prayer' ? 'p' : 'c'}/${targetId}`;
      const resp = await fetch(endpoint);
      if (!resp.ok) throw new Error(`Preview endpoint returned ${resp.status}`);
      const html = await resp.text();
      setMeta(parseMeta(html));
      setPreviewedUrl(shareUrl);
    } catch (e) {
      setError(e.message || 'Could not fetch the preview');
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(previewedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard denied — non-fatal */ }
  };

  if (authLoading) return <div className="p-8 text-center text-zinc-500">Loading…</div>;
  if (!user || user.role !== 'admin') {
    return (
      <div className="max-w-xl mx-auto p-8 text-center">
        <h1 className="text-2xl font-bold mb-2">Admin only</h1>
        <Link to="/" className="text-amber-600 hover:underline">Back to home</Link>
      </div>
    );
  }

  const displayHost = (() => { try { return new URL(previewedUrl || BACKEND).host; } catch { return ''; } })();

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6" data-testid="admin-link-preview-page">
      <div className="flex items-center gap-3 mb-2">
        <Share2 size={22} className="text-amber-500" />
        <h1 className="text-2xl font-bold" data-testid="admin-link-preview-title">Admin · Link Preview</h1>
      </div>
      <p className="text-sm text-zinc-500 mb-6">
        See exactly what a shared link's card will look like on forums, Facebook, X, Discord, Slack and iMessage — rendered from the live crawler tags, before you share it.
      </p>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm mb-4" data-testid="link-preview-error">
          <AlertTriangle size={14} className="inline mr-1" /> {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* -------- Left column: picker -------- */}
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/40 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-3">Link type</div>
            <div className="flex gap-2">
              {['prayer', 'circle'].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  data-testid={`mode-${m}-btn`}
                  className={`h-9 px-4 rounded-full text-sm font-medium capitalize transition-colors ${
                    mode === m
                      ? 'bg-amber-500 text-white'
                      : 'border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800'
                  }`}
                >{m}</button>
              ))}
            </div>

            <label className="block text-xs font-semibold uppercase tracking-wide text-zinc-500 mt-4 mb-2">
              {mode === 'prayer' ? 'Pick a prayer' : 'Pick a member'}
            </label>
            {listLoading ? (
              <div className="text-sm text-zinc-500"><Loader2 size={14} className="animate-spin inline mr-1" /> Loading…</div>
            ) : (
              <select
                value={pickedId}
                onChange={(e) => { setPickedId(e.target.value); setPasted(''); }}
                data-testid="quick-pick-select"
                className="w-full h-10 px-3 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-sm"
              >
                <option value="">— select —</option>
                {mode === 'prayer'
                  ? prayers.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.title}{p.user_handle ? ` — @${p.user_handle}` : ''}{p.visibility && p.visibility !== 'public' ? ` (${p.visibility})` : ''}
                      </option>
                    ))
                  : users.map((u) => (
                      <option key={u.user_id} value={u.user_id}>
                        {u.name || u.handle || u.user_id}{u.handle ? ` — @${u.handle}` : ''}
                      </option>
                    ))}
              </select>
            )}

            <label className="block text-xs font-semibold uppercase tracking-wide text-zinc-500 mt-4 mb-2">
              …or paste a link / ID
            </label>
            <input
              type="text"
              value={pasted}
              onChange={(e) => setPasted(e.target.value)}
              placeholder={mode === 'prayer' ? 'https://…/prayer/abc123 or abc123' : 'https://…/circle/user_x or user_x'}
              data-testid="paste-link-input"
              className="w-full h-10 px-3 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-sm"
            />

            <button
              type="button"
              onClick={doPreview}
              disabled={busy || !targetId}
              data-testid="render-preview-btn"
              className="mt-4 inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-sm font-medium bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
              Render preview
            </button>
          </div>

          {meta && (
            <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/40 p-4" data-testid="share-url-panel">
              <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">Share URL</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-[11px] font-mono text-zinc-600 dark:text-zinc-400 break-all">{previewedUrl}</code>
                <button
                  type="button"
                  onClick={copyLink}
                  data-testid="copy-share-url-btn"
                  title="Copy link"
                  className="shrink-0 p-2 rounded-full border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800"
                >
                  {copied ? <CheckCircle2 size={14} className="text-green-600" /> : <Copy size={14} />}
                </button>
                <a
                  href={previewedUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="open-share-url-link"
                  title="Open link"
                  className="shrink-0 p-2 rounded-full border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800"
                >
                  <ExternalLink size={14} />
                </a>
              </div>
            </div>
          )}
        </div>

        {/* -------- Right column: rendered cards -------- */}
        <div className="lg:col-span-3 space-y-4">
          {!meta ? (
            <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/40 p-4 py-16 text-center text-zinc-500 text-sm" data-testid="preview-empty">
              Pick a {mode} on the left and tap <span className="font-medium">Render preview</span>.
            </div>
          ) : (
            <>
              {/* Large-image card — Facebook / Discord / LinkedIn style */}
              <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/40 p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-3">
                  Large card <span className="normal-case font-normal">(Facebook · Discord · LinkedIn{meta.twitterCard === 'summary_large_image' ? ' · X' : ''})</span>
                </div>
                <div className="max-w-[500px] rounded-lg overflow-hidden border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/60" data-testid="large-card">
                  {meta.image && (
                    <div className="w-full aspect-[1.91/1] bg-zinc-200 dark:bg-zinc-700 overflow-hidden">
                      <img src={meta.image} alt="og" className="w-full h-full object-cover" data-testid="large-card-image" />
                    </div>
                  )}
                  <div className="p-3">
                    <div className="text-[11px] uppercase text-zinc-500 truncate">{displayHost}</div>
                    <div className="font-semibold text-sm mt-0.5 leading-snug" data-testid="large-card-title">{meta.title}</div>
                    <div className="text-xs text-zinc-500 mt-1 line-clamp-2" data-testid="large-card-description">{meta.description}</div>
                  </div>
                </div>
              </div>

              {/* Compact card — forum / Slack / X summary style */}
              <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/40 p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-3">
                  Compact card <span className="normal-case font-normal">(forums · Slack · iMessage{meta.twitterCard === 'summary' ? ' · X' : ''})</span>
                </div>
                <div className="max-w-[500px] flex gap-3 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/60 p-3" data-testid="compact-card">
                  {meta.image && (
                    <div className="w-20 h-20 shrink-0 rounded-md overflow-hidden bg-zinc-200 dark:bg-zinc-700">
                      <img src={meta.image} alt="og" className="w-full h-full object-cover" data-testid="compact-card-image" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-semibold text-sm leading-snug truncate" data-testid="compact-card-title">{meta.title}</div>
                    <div className="text-xs text-zinc-500 mt-1 line-clamp-3" data-testid="compact-card-description">{meta.description}</div>
                    <div className="text-[11px] text-zinc-400 mt-1 truncate">{displayHost}</div>
                  </div>
                </div>
              </div>

              {/* Raw tags */}
              <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/40 p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-3">Raw tags</div>
                <table className="w-full text-xs" data-testid="raw-tags-table">
                  <tbody>
                    {[
                      ['og:title', meta.title],
                      ['og:description', meta.description],
                      ['og:image', meta.image],
                      ['og:url', meta.url],
                      ['og:type', meta.type],
                      ['og:site_name', meta.siteName],
                      ['twitter:card', meta.twitterCard],
                    ].map(([k, v]) => (
                      <tr key={k} className="border-b border-zinc-100 dark:border-zinc-800 last:border-0">
                        <td className="py-1.5 pr-3 font-mono text-zinc-500 whitespace-nowrap align-top">{k}</td>
                        <td className="py-1.5 text-zinc-700 dark:text-zinc-300 break-all">{v || <span className="text-zinc-400">(empty)</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="mt-2 text-[11px] text-zinc-500 leading-snug">
                  Clients cache previews per URL — an already-posted link may keep its old card for a while, but every freshly pasted link uses these tags.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLinkPreview;
